import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const referenceSection: DocsSection = {
  label: "Reference",
  pages: [
    {
      slug: "conventions",
      title: "Conventions",
      description: "Coding standards and the board/SDLC (BF-###).",
      stub: false,
      blocks: [
        {
          kind: "heading",
          id: "code-conventions",
          text: "Code conventions",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            ["Package manager: pnpm, Node >= 22. TypeScript ", { code: "strict" }, "."],
            ["Use ", { code: "import type" }, " for type-only imports."],
            [
              "Nullable fields: ",
              { code: "| null" },
              ", not optional ",
              { code: "?" },
              " — forces explicit handling.",
            ],
            ["No enums — use string-literal unions or ", { code: "as const" }, " objects."],
            ["File naming: kebab-case everywhere. No barrel exports."],
            ["React: function declarations; destructure props in the signature."],
            ["Prettier: double quotes, semicolons, 100-char width, trailing commas."],
          ],
        },
        {
          kind: "heading",
          id: "board",
          text: "Board / SDLC",
        },
        {
          kind: "paragraph",
          text: [
            "All work is tracked on the yangoOS board, so ",
            { strong: "every PR references a board item" },
            " (a ",
            { code: "BF-###" },
            " code) — enforced by the format-only ",
            { code: "board-link" },
            " CI check.",
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Verify with build, not typecheck",
          text: [
            "Run ",
            { code: "pnpm build" },
            " before declaring done — its tsc step catches errors that ",
            { code: "tsc --noEmit" },
            " misses. CI lint runs ",
            { code: "pnpm format:check" },
            ", so format touched files first.",
          ],
        },
      ],
    },
    {
      slug: "known-issues",
      title: "Known issues & caveats",
      description: "Current gotchas — kept honest and current.",
      stub: false,
      blocks: [
        {
          kind: "callout",
          tone: "warn",
          title: "dev default company",
          text: [
            "dev's default company is ",
            { code: "Moveo (Demo)" },
            " — being reconciled. Resolve the default company at runtime.",
          ],
        },
        {
          kind: "callout",
          tone: "success",
          title: "BF-177 — migrate gate now runs the freshly-built image (resolved)",
          text: "The deploy-erpnext migrate gate no longer runs the migrate family's baked-in bootstrap tag. It registers a new revision swapping only the migrate container's image to this deploy's :git-<sha> and runs that — so a CI deploy always applies new customizations. See Build & Deploy → CI/CD.",
        },
        {
          kind: "callout",
          tone: "warn",
          title: "Courimax webhook — the dev ALB is office-IP-only",
          text: [
            "The inbound courier webhook (",
            { code: "/api/dispatch/events" },
            ") sits behind the shared office-IP-only ALB on dev and staging, so Courimax's own servers cannot reach it until their egress IPs are allow-listed (or a path-scoped rule is added). The webhook URLs are ",
            { code: "https://api-dev.yango.moveodevelop.com/api/dispatch/events" },
            " (dev) and ",
            { code: "https://api-staging.yango.moveodevelop.com/api/dispatch/events" },
            " (staging). See API Integration → Courimax.",
          ],
        },
        {
          kind: "callout",
          tone: "danger",
          title: "Courimax — release-order the customizations image first",
          text: [
            "A dispatch write to a Custom Field that is not yet migrated is silently 2xx-dropped by Frappe. Bake the ",
            { code: "yango-customizations" },
            " release with the dispatch fields into the ERPNext image and migrate it BEFORE setting ",
            { code: "COURIER_PROVIDER=courimax" },
            " in apps/api — otherwise every event is recorded as ",
            { code: "dispatch-state-unwritable" },
            ".",
          ],
        },
      ],
    },
  ],
};
